import { Component, EventEmitter, OnInit, Output } from '@angular/core'

import { WorklogService } from '../../data/services/worklog.service'
import { WorkItem, WorkItemArea, WorkItemType, WorkUserRef } from '../../data/interfaces/worklog.interface'
import { AREA_META, AREA_ORDER, ORIGIN_META, TYPE_META } from '../../data/constants/worklog.constants'

/**
 * Vista Historial: tareas en estado terminal —entregadas (`Cerrada`) y descartadas
 * (`Cancelada`)— para consulta y trazabilidad. Buscable por folio o título y filtrable por
 * tipo/área/sprint/desenlace. Vive fuera de Backlog y Tablero para no ensuciar la operación.
 *
 * Es además el ÚNICO sitio donde reaparece una tarea cancelada: no está en el Backlog (no es
 * `Backlog`) ni en el Tablero (no es una de las 9 columnas). Desde su detalle se puede reabrir.
 */
@Component({
  selector: 'app-worklog-history',
  templateUrl: './worklog-history.component.html',
  styleUrls: ['./worklog-history.component.scss'],
})
export class WorklogHistoryComponent implements OnInit {
  @Output() openItem = new EventEmitter<string>()

  all: WorkItem[] = []
  items: WorkItem[] = []
  loading = true

  search = ''
  filterType: WorkItemType | null = null
  filterArea: WorkItemArea | null = null
  filterSprint: string | null = null
  filterOutcome: 'Cerrada' | 'Cancelada' | null = null

  sprintOptions: { label: string; value: string }[] = []

  readonly TYPE_META = TYPE_META
  readonly AREA_META = AREA_META
  readonly ORIGIN_META = ORIGIN_META
  readonly typeOptions = [
    { label: 'Tareas', value: 'task' },
    { label: 'Bugs', value: 'bug' },
  ]
  readonly areaOptions = AREA_ORDER.map((a) => ({ label: AREA_META[a].label, value: a }))
  readonly outcomeOptions = [
    { label: 'Entregadas', value: 'Cerrada' },
    { label: 'Canceladas', value: 'Cancelada' },
  ]

  constructor(private worklog: WorklogService) {}

  ngOnInit(): void {
    this.loading = true
    this.worklog.getFinishedTasks(1, 1000, {}).subscribe((page) => {
      this.all = page.data || []
      // Sprints presentes (de legacy.sprint del histórico) para el filtro.
      const sprints = Array.from(new Set(this.all.map((it) => this.sprintOf(it)).filter(Boolean))).sort(
        (a, b) => parseFloat(a) - parseFloat(b)
      )
      this.sprintOptions = sprints.map((s) => ({ label: 'Sprint ' + s, value: s }))
      this.apply()
      this.loading = false
    })
  }

  apply(): void {
    const q = this.search.trim().toLowerCase()
    this.items = this.all.filter(
      (it) =>
        (!this.filterType || it.type === this.filterType) &&
        (!this.filterArea || (it.areas || []).includes(this.filterArea)) &&
        (!this.filterSprint || this.sprintOf(it) === this.filterSprint) &&
        (!this.filterOutcome || it.status === this.filterOutcome) &&
        //* La búsqueda cubre el folio: es como se identifica una tarea al encargarla.
        (!q || it.title.toLowerCase().includes(q) || (it.code || '').toLowerCase().includes(q))
    )
  }

  clear(): void {
    this.search = ''
    this.filterType = null
    this.filterArea = null
    this.filterSprint = null
    this.filterOutcome = null
    this.apply()
  }

  sprintOf(it: WorkItem): string {
    return (it.criteria && it.criteria['legacy'] && it.criteria['legacy'].sprint) || ''
  }

  completedAt(it: WorkItem): string | undefined {
    return (it.criteria && it.criteria['legacy'] && it.criteria['legacy'].date) || it.updatedAt || undefined
  }

  areaMeta(a: string) {
    return (AREA_META as any)[a]
  }

  userName(u?: WorkUserRef | string | null): string {
    if (!u || typeof u === 'string') return '—'
    return `${u.names ?? ''} ${u.lastNames ?? ''}`.trim() || u.email || '—'
  }

  initials(u?: WorkUserRef | string | null): string {
    if (!u || typeof u === 'string') return '·'
    return `${(u.names ?? '?')[0] ?? ''}${(u.lastNames ?? '')[0] ?? ''}`.toUpperCase()
  }

  trackId(_: number, it: WorkItem): string {
    return it._id
  }
}
